// Moderation feature admin screen (docs/MODERATION.md §4/§5). Queue + case view + audited actions
// for the admin portal, rendered through the feature screen registry (web/src/features/registry.ts)
// and mirroring the Admissions screen's conventions (shared layout/help components, permission
// gating via the catalog's permitted `actions`, accessible plain-language controls).
//
// Brand- and service-neutral: NOTHING OneHook-specific is hard-coded. Category codes, labels, SLAs
// and self-harm resources come from the server (the queue/case API) and config; only platform-level
// enums (severity tiers, case statuses, account states, sort keys, the fixed action set and the
// structured MOD_* reason codes) are referenced here, exactly as they are defined by the feature
// contract (features/moderation/api). Filters travel in the request body; filter state round-trips
// through the URL; the queue pages with an opaque cursor stack (prev/next).
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useSearchParams } from "react-router-dom";
import type { FeatureScreenProps, FeatureScreenAction } from "../../../web/src/features/contract.js";
import { PageHelp } from "../../../web/src/layout/PageHelp.js";

// ── platform-level enums (from the feature contract, NOT brand config) ────────────────────────────

const STATUSES = ["open", "in-review", "awaiting-info", "actioned", "dismissed"] as const;
const SEVERITIES = ["critical", "high", "standard"] as const;
const ACCOUNT_STATES = ["ACTIVE", "RESTRICTED", "SUSPENDED", "BANNED"] as const;
const SORT_KEYS = ["priority", "slaDueAt", "lastReportAt", "openedAt", "totalReports"] as const;

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;

/** Plain-language meaning of each case status (platform concept, not brand-specific). */
const STATUS_HELP: Record<string, string> = {
  open: "New cases nobody has started yet.",
  "in-review": "A moderator has picked the case up and is working on it.",
  "awaiting-info": "Waiting on more information (e.g. an appeal or a request for detail).",
  actioned: "An enforcement action was taken and the case is closed.",
  dismissed: "Reviewed and closed with no violation found.",
};

/** The fixed moderation action set (features/moderation/api). `punitive` ones enforce against a member. */
interface ActionDef {
  id: string;
  label: string;
  help: string;
  punitive: boolean;
  /** Only offered on a support/welfare case (e.g. self-harm). */
  supportOnly?: boolean;
  /** Requires a positive duration in days (restrict). */
  needsDuration?: boolean;
  /** Requires an "until" date (suspend). */
  needsUntil?: boolean;
  /** Requires a two-step confirmation dialog (suspend / ban). */
  confirm?: boolean;
}

const ACTION_DEFS: ActionDef[] = [
  { id: "dismiss", label: "Dismiss (no violation)", help: "Close the case with no action. Feeds reporter trust.", punitive: false },
  { id: "warn", label: "Warn", help: "Send the member a warning. No change to what they can do.", punitive: true },
  { id: "restrict", label: "Restrict", help: "Hide the member from discovery for a number of days.", punitive: true, needsDuration: true },
  { id: "suspend", label: "Suspend", help: "Block sign-in until a date you pick.", punitive: true, needsUntil: true, confirm: true },
  { id: "propose-ban", label: "Propose ban", help: "Start a permanent ban. A second moderator must confirm it (two-person rule).", punitive: true, confirm: true },
  { id: "escalate", label: "Escalate", help: "Route to the safety lead (critical / legal / CSAM).", punitive: false },
  { id: "request-info", label: "Request info", help: "Set the case to awaiting-info while you gather more detail.", punitive: false },
  { id: "reopen", label: "Reopen", help: "Re-open a closed case.", punitive: false },
  { id: "resolve-supported", label: "Resolve (support provided)", help: "Close a welfare case after offering support. Never punitive.", punitive: false, supportOnly: true },
];

/** Structured, PII-free reason codes per action (MOD_* platform convention; the note carries detail). */
const REASON_CODES: Record<string, string[]> = {
  dismiss: ["MOD_DISMISS", "MOD_NO_VIOLATION", "MOD_FALSE_REPORT", "MOD_OTHER"],
  warn: ["MOD_WARN", "MOD_MINOR_VIOLATION", "MOD_OTHER"],
  restrict: ["MOD_RESTRICT", "MOD_REPEATED_VIOLATION", "MOD_OTHER"],
  suspend: ["MOD_SUSPEND", "MOD_SERIOUS_VIOLATION", "MOD_OTHER"],
  "propose-ban": ["MOD_BAN", "MOD_SEVERE_VIOLATION", "MOD_OTHER"],
  escalate: ["MOD_ESCALATE", "MOD_SAFETY_LEAD", "MOD_LEGAL", "MOD_OTHER"],
  "request-info": ["MOD_REQUEST_INFO", "MOD_OTHER"],
  reopen: ["MOD_REOPEN", "MOD_OTHER"],
  "resolve-supported": ["MOD_SUPPORT_RESOLVED", "MOD_OTHER"],
  "ban-confirm": ["MOD_BAN", "MOD_OTHER"],
  "ban-reject": ["MOD_BAN_REJECTED", "MOD_OTHER"],
  "appeal-decide": ["MOD_APPEAL", "MOD_OTHER"],
};

// ── server response views ────────────────────────────────────────────────────────────────────────

export interface CaseView {
  caseId: string;
  subjectId: string;
  version?: number;
  status: string;
  priority: number;
  severityTop: string;
  hasCritical: boolean;
  escalated: boolean;
  slaDueAt: number;
  openedAt: number;
  lastReportAt: number;
  totalReports: number;
  distinctReporters: number;
  distinctBlockers: number;
  categories: Record<string, number>;
  sources: string[];
  hasEvidence: boolean;
  hasReciprocal: boolean;
  topReporterTrustBucket: "low" | "medium" | "high";
  accountState: string;
  accountStateVersion?: number;
  accountStateUntil?: number;
  /** Mirrored preservation hold (PII-free); drives the case/queue hold badge. */
  preservationHold?: PreservationHoldView;
  assignedTo?: string;
  reopenedCount: number;
  banProposalPending: boolean;
  appealStatus?: string;
  lastAction?: string;
  lastActionAt?: number;
  /** Per-evidence summaries (metadata only) returned by the `case` surface; drives the evidence viewer. */
  evidence?: EvidenceSummary[];
  /**
   * M2: server-authoritative evidence suppression. True when the case contains any minorSafety
   * category report, in which case the decrypted-chat evidence is never opened in the portal (the
   * evidence GET surface also rejects 409 EVIDENCE_SUPPRESSED). The UI mirrors this exact rule.
   */
  evidenceSuppressed?: boolean;
}

/** Metadata-only summary of one uploaded evidence bundle (the `case` surface returns these). */
export interface EvidenceSummary {
  evidenceId: string;
  uploadedAt: number;
  includesMedia: boolean;
  messageCount: number;
  firstSentAt: number;
  lastSentAt: number;
  sizeBytes: number;
  legalHold: boolean;
  retentionState: string;
}

/** One chat message inside a decrypted-chat evidence bundle. */
export interface EvidenceMessage {
  messageId: string;
  sentAt: string;
  senderSide: "reporter" | "reported";
  text: string;
}

/** The full response of the GET `evidence` surface (server re-hashes the bytes; `integrity` is its verdict). */
export interface EvidenceDetail {
  integrity: "verified" | "mismatch";
  metadata: {
    evidenceId: string;
    sha256: string;
    sizeBytes: number;
    messageIds: string[];
    firstSentAt: number;
    lastSentAt: number;
    includesMedia: boolean;
    uploadedAt: number;
    legalHold: boolean;
    retentionState: string;
  };
  bundle: {
    v: number;
    reportId: string;
    conversationId: string;
    window: { maxMessages: number; maxDays: number };
    messages: EvidenceMessage[];
    attachments: unknown[];
  };
}

interface ReportViewItem {
  reportId: string;
  category: string;
  severity: string;
  alsoBlocked?: boolean;
  evidenceOffered?: boolean;
  conversationId?: string;
  source?: string;
  reportedAt: number;
  outcome?: string;
  reporterTrustBucket: string;
  detail?: unknown;
}

interface HistoryItem {
  at: number;
  actorId: string;
  kind: string;
  action?: string;
  reason?: string;
  detail?: Record<string, unknown>;
}

interface AccountStateView {
  state: string;
  version: number;
  until?: number;
  reasonCode?: string;
}

interface SupportResource {
  region?: string;
  name: string;
  contact: string;
}

interface CaseResponse {
  case: CaseView;
  reports: ReportViewItem[];
  signals?: unknown;
  accountState?: AccountStateView;
  /** Live preservation hold (authoritative, read from Identity by the `case` surface). */
  preservationHold?: PreservationHoldView;
  history: HistoryItem[];
  supportResources: SupportResource[];
}

/** PII-free preservation-hold view (reason CODE + hashed caseRef only) shown on the case. */
export interface PreservationHoldView {
  active: boolean;
  reasonCode?: string;
  until?: number;
  version?: number;
  changedAt?: number;
  setBy?: string;
  /** Epoch ms the current hold was first set; drives the CHILD_SAFETY 180-day release minimum (MEDIUM-1). */
  setAt?: number;
  /**
   * Epoch ms the current CHILD_SAFETY hold was first set, as reported authoritatively by Identity.
   * Preferred over `setAt` for the 180-day minimum when present (task item 3).
   */
  childSafetySince?: number;
  caseRef?: string;
}

interface QueueResponse {
  items: CaseView[];
  nextCursor?: string;
  total: number;
}

// ── filter state (serialised to the URL; mapped to the request body) ──────────────────────────────

export interface FilterState {
  status: string[];
  severity: string[];
  category: string[];
  accountState: string[];
  minReportCount: string;
  maxReportCount: string;
  minDistinctReporters: string;
  maxDistinctReporters: string;
  frequencyWindowDays: string;
  minInWindow: string;
  trustMin: string;
  trustMax: string;
  minAccountAgeDays: string;
  maxAccountAgeDays: string;
  slaDueWithinHours: string;
  slaBreached: "" | "true" | "false";
  hasEvidence: "" | "true" | "false";
  reciprocal: "" | "true" | "false";
  assignment: "anyone" | "me" | "unassigned" | "specific";
  assignedTo: string;
  source: string;
  openedAfter: string;
  openedBefore: string;
  lastReportAfter: string;
  lastReportBefore: string;
  sortKey: string;
  sortDir: "asc" | "desc";
}

export const DEFAULT_FILTERS: FilterState = {
  status: [],
  severity: [],
  category: [],
  accountState: [],
  minReportCount: "",
  maxReportCount: "",
  minDistinctReporters: "",
  maxDistinctReporters: "",
  frequencyWindowDays: "",
  minInWindow: "",
  trustMin: "",
  trustMax: "",
  minAccountAgeDays: "",
  maxAccountAgeDays: "",
  slaDueWithinHours: "",
  slaBreached: "",
  hasEvidence: "",
  reciprocal: "",
  assignment: "anyone",
  assignedTo: "",
  source: "",
  openedAfter: "",
  openedBefore: "",
  lastReportAfter: "",
  lastReportBefore: "",
  sortKey: "priority",
  sortDir: "desc",
};

const ARRAY_KEYS = ["status", "severity", "category", "accountState"] as const;

/** Serialise non-default filter state to flat URL params (arrays comma-joined). */
export function serializeFilters(f: FilterState): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of ARRAY_KEYS) {
    if (f[k].length > 0) out[k] = f[k].join(",");
  }
  (Object.keys(DEFAULT_FILTERS) as Array<keyof FilterState>).forEach((k) => {
    if ((ARRAY_KEYS as readonly string[]).includes(k as string)) return;
    const v = f[k] as string;
    if (v !== "" && v !== DEFAULT_FILTERS[k]) out[k] = v;
  });
  return out;
}

/** Parse URL params back into a full filter state (missing keys fall back to defaults). */
export function parseFilters(params: URLSearchParams): FilterState {
  const f: FilterState = { ...DEFAULT_FILTERS, status: [], severity: [], category: [], accountState: [] };
  for (const k of ARRAY_KEYS) {
    const raw = params.get(k);
    f[k] = raw ? raw.split(",").filter(Boolean) : [];
  }
  (Object.keys(DEFAULT_FILTERS) as Array<keyof FilterState>).forEach((k) => {
    if ((ARRAY_KEYS as readonly string[]).includes(k as string)) return;
    const raw = params.get(k as string);
    if (raw !== null) (f as unknown as Record<string, unknown>)[k as string] = raw;
  });
  return f;
}

function numOrUndef(s: string): number | undefined {
  if (s.trim() === "") return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

/** Parse a <input type="date"> value (YYYY-MM-DD) to epoch ms at UTC midnight. */
function dateToMs(s: string): number | undefined {
  if (!s) return undefined;
  const ms = Date.parse(`${s}T00:00:00Z`);
  return Number.isFinite(ms) ? ms : undefined;
}

function boolOrUndef(v: "" | "true" | "false"): boolean | undefined {
  return v === "" ? undefined : v === "true";
}

/** The sort spec the queue surface expects. */
export function sortFromFilters(f: FilterState): { key: string; dir: "asc" | "desc" } {
  const key = (SORT_KEYS as readonly string[]).includes(f.sortKey) ? f.sortKey : "priority";
  return { key, dir: f.sortDir === "asc" ? "asc" : "desc" };
}

/**
 * Map the UI filter state onto the server QueueFilter (docs/MODERATION.md §4). Empty inputs are
 * omitted; the `assignment` selector expands to assignedTo / unassigned; UI-friendly units (SLA due
 * within hours, date pickers) convert to the server's milliseconds / epoch.
 */
export function buildQueueFilter(f: FilterState, currentActorId?: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (f.status.length) out.status = f.status;
  if (f.severity.length) out.severity = f.severity;
  if (f.category.length) out.category = f.category;
  if (f.accountState.length) out.accountState = f.accountState;

  const numPairs: Array<[keyof FilterState, string]> = [
    ["minReportCount", "minReportCount"],
    ["maxReportCount", "maxReportCount"],
    ["minDistinctReporters", "minDistinctReporters"],
    ["maxDistinctReporters", "maxDistinctReporters"],
    ["frequencyWindowDays", "frequencyWindowDays"],
    ["minInWindow", "minInWindow"],
    ["trustMin", "trustMin"],
    ["trustMax", "trustMax"],
    ["minAccountAgeDays", "minAccountAgeDays"],
    ["maxAccountAgeDays", "maxAccountAgeDays"],
  ];
  for (const [src, dest] of numPairs) {
    const n = numOrUndef(f[src] as string);
    if (n !== undefined) out[dest] = n;
  }
  const dueWithin = numOrUndef(f.slaDueWithinHours);
  if (dueWithin !== undefined) out.slaDueWithinMs = dueWithin * HOUR_MS;

  const sla = boolOrUndef(f.slaBreached);
  if (sla !== undefined) out.slaBreached = sla;
  const ev = boolOrUndef(f.hasEvidence);
  if (ev !== undefined) out.hasEvidence = ev;
  const rec = boolOrUndef(f.reciprocal);
  if (rec !== undefined) out.reciprocal = rec;

  if (f.assignment === "unassigned") out.unassigned = true;
  else if (f.assignment === "me" && currentActorId) out.assignedTo = currentActorId;
  else if (f.assignment === "specific" && f.assignedTo.trim()) out.assignedTo = f.assignedTo.trim();

  if (f.source.trim()) out.source = f.source.trim();

  const dateMap: Array<[keyof FilterState, string]> = [
    ["openedAfter", "openedAfter"],
    ["openedBefore", "openedBefore"],
    ["lastReportAfter", "lastReportAfter"],
    ["lastReportBefore", "lastReportBefore"],
  ];
  for (const [src, dest] of dateMap) {
    const ms = dateToMs(f[src] as string);
    if (ms !== undefined) out[dest] = ms;
  }
  return out;
}

/** Build the full queue request body (filter + sort + limit + optional cursor). */
export function buildQueueRequest(
  f: FilterState,
  currentActorId: string | undefined,
  limit: number,
  cursor?: string
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    filter: buildQueueFilter(f, currentActorId),
    sort: sortFromFilters(f),
    limit,
  };
  if (cursor) body.cursor = cursor;
  return body;
}

// ── small display helpers ─────────────────────────────────────────────────────────────────────────

/** Short, non-identifying rendering of a subject id (keeps the full id for calls). */
export function shortId(id: string): string {
  if (id.length <= 12) return id;
  return `${id.slice(0, 6)}…${id.slice(-4)}`;
}

export function formatAge(from: number, now: number): string {
  const ms = Math.max(0, now - from);
  const days = Math.floor(ms / DAY_MS);
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(ms / HOUR_MS);
  if (hours >= 1) return `${hours}h`;
  const mins = Math.floor(ms / 60000);
  return `${mins}m`;
}

/** SLA state: a word + symbol (never colour alone) for the queue + case view. */
export function slaState(dueAt: number, now: number): { tone: "breached" | "due" | "ok"; text: string } {
  if (now > dueAt) return { tone: "breached", text: "⚠ Breached" };
  if (dueAt - now <= 2 * HOUR_MS) return { tone: "due", text: "◴ Due soon" };
  return { tone: "ok", text: "On time" };
}

function fmtTime(ms?: number): string {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return "—";
  return new Date(ms).toISOString().replace("T", " ").replace(/\.\d+Z$/, "Z");
}

function hasAction(actions: FeatureScreenAction[], id: string): boolean {
  return actions.some((a) => a.id === id);
}

function hasPermissionSuffix(actions: FeatureScreenAction[], suffix: string): boolean {
  return actions.some((a) => a.permission.endsWith(`:${suffix}`));
}

/** The pending ban proposer, derived from the audited history (caseView does not expose it). */
export function pendingBanProposer(history: HistoryItem[]): string | undefined {
  let proposer: string | undefined;
  for (const h of history) {
    if (h.kind === "ban-proposed") proposer = h.actorId;
    else if (h.kind === "ban-confirmed" || h.kind === "ban-rejected") proposer = undefined;
  }
  return proposer;
}

/** Original actors on the enforcement being appealed (decide-appeal must not be any of them, M6). */
export function appealOriginalActors(history: HistoryItem[]): Set<string> {
  const actors = new Set<string>();
  for (const h of history) {
    if (h.kind === "appeal-submitted") break; // only actions BEFORE the appeal count
    if (h.kind === "action" || h.kind === "ban-proposed" || h.kind === "ban-confirmed") actors.add(h.actorId);
  }
  return actors;
}

// ── severity badge (text + symbol, colour is secondary) ──────────────────────────────────────────

function SeverityBadge({ tier }: { tier: string }): ReactElement {
  const symbol = tier === "critical" ? "●●●" : tier === "high" ? "●●" : "●";
  const cls = tier === "critical" ? "danger-high" : tier === "high" ? "danger-medium" : "";
  return (
    <span className={cls} aria-label={`Severity: ${tier}`}>
      <span aria-hidden="true">{symbol} </span>
      {tier}
    </span>
  );
}

// ── the screen ──────────────────────────────────────────────────────────────────────────────────

/** Moderation workspace: Queue (filters + table + pagination) and Case view (reports + actions). */
export function ModerationScreen({ actions, call, currentActorId }: FeatureScreenProps): ReactElement {
  const [params, setParams] = useSearchParams();
  const committed = useMemo(() => parseFilters(params), [params]);
  const paramsKey = params.toString();

  const [draft, setDraft] = useState<FilterState>(committed);
  const [items, setItems] = useState<CaseView[]>([]);
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [prevCursors, setPrevCursors] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [queueError, setQueueError] = useState<string | null>(null);

  const [selected, setSelected] = useState<CaseResponse | null>(null);
  const [caseError, setCaseError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const canView = hasAction(actions, "queue");
  const canAct = hasAction(actions, "action");
  const canAssign = hasAction(actions, "assign");
  const canBanConfirm = hasAction(actions, "ban-confirm");
  const canSubmitAppeal = hasAction(actions, "appeal-submit");
  const canDecideAppeal = hasAction(actions, "appeal-decide");
  const canViewEvidence = hasPermissionSuffix(actions, "view-evidence");
  const canLegalHold = hasPermissionSuffix(actions, "legal-hold");
  const canPreservationHold = hasPermissionSuffix(actions, "preservation-hold");

  // Re-sync the editable draft whenever the committed (URL) filters change (e.g. a quick filter).
  useEffect(() => {
    setDraft(committed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  const loadQueue = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setQueueError(null);
    try {
      const body = buildQueueRequest(parseFilters(new URLSearchParams(paramsKey)), currentActorId, 50, cursor);
      const res = (await call("queue", "GET", body)) as QueueResponse;
      setItems(res.items ?? []);
      setTotal(res.total ?? res.items?.length ?? 0);
      setNextCursor(res.nextCursor);
    } catch (e) {
      setQueueError(e instanceof Error ? e.message : "Failed to load the queue");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [call, canView, currentActorId, paramsKey, cursor]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  function applyFilters(next: FilterState): void {
    setCursor(undefined);
    setPrevCursors([]);
    setParams(serializeFilters(next));
  }

  function quickFilter(preset: Partial<FilterState>): void {
    const next: FilterState = { ...DEFAULT_FILTERS, ...preset };
    setDraft(next);
    applyFilters(next);
  }

  function goNext(): void {
    if (!nextCursor) return;
    setPrevCursors((s) => [...s, cursor ?? ""]);
    setCursor(nextCursor);
  }
  function goPrev(): void {
    setPrevCursors((s) => {
      if (s.length === 0) return s;
      const copy = [...s];
      const last = copy.pop();
      setCursor(last === "" ? undefined : last);
      return copy;
    });
  }

  const openCase = useCallback(
    async (subjectId: string) => {
      setCaseError(null);
      setNotice(null);
      try {
        const res = (await call("case", "GET", { subjectId })) as CaseResponse;
        setSelected(res);
      } catch (e) {
        setCaseError(e instanceof Error ? e.message : "Failed to open the case");
      }
    },
    [call]
  );

  const reloadCase = useCallback(
    async (subjectId: string): Promise<CaseResponse | null> => {
      try {
        const res = (await call("case", "GET", { subjectId })) as CaseResponse;
        setSelected(res);
        return res;
      } catch (e) {
        setCaseError(e instanceof Error ? e.message : "Failed to reload the case");
        return null;
      }
    },
    [call]
  );

  if (!canView) {
    return (
      <section aria-labelledby="mod-title">
        <h1 id="mod-title">Moderation</h1>
        <p role="alert">You do not have permission to view the moderation queue.</p>
      </section>
    );
  }

  const discoveredCategories = Array.from(new Set(items.flatMap((i) => Object.keys(i.categories)))).sort();

  return (
    <section aria-labelledby="mod-title">
      <h1 id="mod-title">Moderation</h1>
      <PageHelp>
        <p>
          Reports against a member are grouped into one <strong>case</strong>, ordered by priority. Work the queue, open a
          case to see the reports and signals, then take an action. Every action needs a reason and is recorded.
        </p>
        <ul>
          <li><strong>Queue</strong>: filter and sort cases. Critical safety cases are always at the top.</li>
          <li><strong>Case</strong>: the reports, the member&apos;s signals and account state, the history, and the actions you can take.</li>
        </ul>
      </PageHelp>

      <p className="sr-only" role="status" aria-live="polite">
        {loading ? "Loading cases" : `${items.length} of ${total} cases shown`}
      </p>
      {notice ? (
        <p role="status" aria-live="polite">
          {notice}
        </p>
      ) : null}

      <QuickFilters onPick={quickFilter} canUseMine={!!currentActorId} />

      <FilterPanel
        draft={draft}
        setDraft={setDraft}
        discoveredCategories={discoveredCategories}
        onApply={() => applyFilters(draft)}
        onReset={() => quickFilter({})}
      />

      {queueError ? <p role="alert">{queueError}</p> : null}

      <QueueTable items={items} loading={loading} now={Date.now()} selectedSubject={selected?.case.subjectId} onOpen={openCase} />

      <nav aria-label="Queue pagination" className="action-bar">
        <button type="button" onClick={goPrev} disabled={prevCursors.length === 0}>
          ← Previous
        </button>
        <button type="button" onClick={goNext} disabled={!nextCursor}>
          Next →
        </button>
      </nav>

      {caseError ? <p role="alert">{caseError}</p> : null}

      {selected ? (
        <CasePanel
          data={selected}
          currentActorId={currentActorId}
          perms={{ canAct, canAssign, canBanConfirm, canSubmitAppeal, canDecideAppeal, canViewEvidence, canLegalHold, canPreservationHold }}
          onClose={() => setSelected(null)}
          onNotice={setNotice}
          onError={setCaseError}
          reloadCase={reloadCase}
          reloadQueue={loadQueue}
          call={call}
        />
      ) : null}
    </section>
  );
}

// ── quick (saved) filters ─────────────────────────────────────────────────────────────────────────

function QuickFilters({ onPick, canUseMine }: { onPick: (preset: Partial<FilterState>) => void; canUseMine: boolean }): ReactElement {
  return (
    <nav aria-label="Saved quick filters" className="action-bar">
      <span id="quick-label">Quick filters:</span>
      <button type="button" aria-describedby="quick-label" onClick={() => onPick({ severity: ["critical"] })}>Critical</button>
      <button type="button" aria-describedby="quick-label" onClick={() => onPick({ slaBreached: "true" })}>SLA breaching</button>
      <button type="button" aria-describedby="quick-label" onClick={() => onPick({ assignment: "unassigned" })}>Unassigned</button>
      <button type="button" aria-describedby="quick-label" onClick={() => onPick({ assignment: "me" })} disabled={!canUseMine}>Mine</button>
    </nav>
  );
}

// ── filter panel ────────────────────────────────────────────────────────────────────────────────

function MultiCheck({
  legend,
  options,
  selected,
  onChange,
  idPrefix,
}: {
  legend: string;
  options: readonly string[];
  selected: string[];
  onChange: (next: string[]) => void;
  idPrefix: string;
}): ReactElement {
  return (
    <fieldset>
      <legend>{legend}</legend>
      {options.map((opt) => {
        const id = `${idPrefix}-${opt}`;
        const checked = selected.includes(opt);
        return (
          <label key={opt} htmlFor={id} style={{ marginRight: 12 }}>
            <input
              id={id}
              type="checkbox"
              checked={checked}
              onChange={(e) => onChange(e.target.checked ? [...selected, opt] : selected.filter((s) => s !== opt))}
            />{" "}
            {opt}
          </label>
        );
      })}
    </fieldset>
  );
}

function NumField({
  id,
  label,
  value,
  onChange,
  step,
  min,
  max,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  step?: string;
  min?: number;
  max?: number;
}): ReactElement {
  return (
    <span className="field" style={{ display: "inline-flex", marginRight: 12 }}>
      <label htmlFor={id}>{label}</label>
      <input id={id} type="number" value={value} step={step} min={min} max={max} onChange={(e) => onChange(e.target.value)} />
    </span>
  );
}

function TriSelect({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: "" | "true" | "false") => void;
}): ReactElement {
  return (
    <span className="field" style={{ display: "inline-flex", marginRight: 12 }}>
      <label htmlFor={id}>{label}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value as "" | "true" | "false")}>
        <option value="">Any</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    </span>
  );
}

function FilterPanel({
  draft,
  setDraft,
  discoveredCategories,
  onApply,
  onReset,
}: {
  draft: FilterState;
  setDraft: (f: FilterState) => void;
  discoveredCategories: string[];
  onApply: () => void;
  onReset: () => void;
}): ReactElement {
  const set = (patch: Partial<FilterState>): void => setDraft({ ...draft, ...patch });

  return (
    <form
      aria-label="Queue filters"
      onSubmit={(e) => {
        e.preventDefault();
        onApply();
      }}
    >
      <MultiCheck legend="Status" options={STATUSES} selected={draft.status} onChange={(v) => set({ status: v })} idPrefix="f-status" />
      <MultiCheck legend="Severity" options={SEVERITIES} selected={draft.severity} onChange={(v) => set({ severity: v })} idPrefix="f-sev" />
      <MultiCheck legend="Account state" options={ACCOUNT_STATES} selected={draft.accountState} onChange={(v) => set({ accountState: v })} idPrefix="f-acct" />

      <fieldset>
        <legend>Categories</legend>
        <span className="field">
          <label htmlFor="f-category">Category codes (comma-separated)</label>
          <input
            id="f-category"
            type="text"
            value={draft.category.join(",")}
            aria-describedby="f-category-hint"
            onChange={(e) => set({ category: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
          />
          <span id="f-category-hint" className="field-hint">Codes come from the report categories your service configures. Add any below.</span>
        </span>
        {discoveredCategories.length > 0 ? (
          <div>
            <span className="field-hint">Seen in results (click to toggle):</span>{" "}
            {discoveredCategories.map((cat) => {
              const on = draft.category.includes(cat);
              return (
                <button
                  key={cat}
                  type="button"
                  aria-pressed={on}
                  onClick={() => set({ category: on ? draft.category.filter((x) => x !== cat) : [...draft.category, cat] })}
                >
                  {on ? "✓ " : "+ "}
                  {cat}
                </button>
              );
            })}
          </div>
        ) : null}
      </fieldset>

      <fieldset>
        <legend>Counts &amp; trust</legend>
        <NumField id="f-minrep" label="Min reports" value={draft.minReportCount} onChange={(v) => set({ minReportCount: v })} min={0} />
        <NumField id="f-maxrep" label="Max reports" value={draft.maxReportCount} onChange={(v) => set({ maxReportCount: v })} min={0} />
        <NumField id="f-mindr" label="Min distinct reporters" value={draft.minDistinctReporters} onChange={(v) => set({ minDistinctReporters: v })} min={0} />
        <NumField id="f-maxdr" label="Max distinct reporters" value={draft.maxDistinctReporters} onChange={(v) => set({ maxDistinctReporters: v })} min={0} />
        <NumField id="f-trustmin" label="Trust min" value={draft.trustMin} onChange={(v) => set({ trustMin: v })} step="0.01" min={0} max={1} />
        <NumField id="f-trustmax" label="Trust max" value={draft.trustMax} onChange={(v) => set({ trustMax: v })} step="0.01" min={0} max={1} />
      </fieldset>

      <fieldset>
        <legend>Frequency window</legend>
        <NumField id="f-freqwin" label="Window (days)" value={draft.frequencyWindowDays} onChange={(v) => set({ frequencyWindowDays: v })} min={1} />
        <NumField id="f-mininwin" label="Min reports in window" value={draft.minInWindow} onChange={(v) => set({ minInWindow: v })} min={0} />
      </fieldset>

      <fieldset>
        <legend>Account age</legend>
        <NumField id="f-minage" label="Min age (days)" value={draft.minAccountAgeDays} onChange={(v) => set({ minAccountAgeDays: v })} min={0} />
        <NumField id="f-maxage" label="Max age (days)" value={draft.maxAccountAgeDays} onChange={(v) => set({ maxAccountAgeDays: v })} min={0} />
      </fieldset>

      <fieldset>
        <legend>SLA</legend>
        <TriSelect id="f-slabreached" label="SLA breached" value={draft.slaBreached} onChange={(v) => set({ slaBreached: v })} />
        <NumField id="f-sladue" label="Due within (hours)" value={draft.slaDueWithinHours} onChange={(v) => set({ slaDueWithinHours: v })} min={0} />
      </fieldset>

      <fieldset>
        <legend>Signals</legend>
        <TriSelect id="f-hasev" label="Has evidence" value={draft.hasEvidence} onChange={(v) => set({ hasEvidence: v })} />
        <TriSelect id="f-recip" label="Reciprocal" value={draft.reciprocal} onChange={(v) => set({ reciprocal: v })} />
        <span className="field" style={{ display: "inline-flex", marginRight: 12 }}>
          <label htmlFor="f-source">Source</label>
          <input id="f-source" type="text" value={draft.source} onChange={(e) => set({ source: e.target.value })} />
        </span>
      </fieldset>

      <fieldset>
        <legend>Assignment</legend>
        <span className="field" style={{ display: "inline-flex", marginRight: 12 }}>
          <label htmlFor="f-assign">Assigned to</label>
          <select id="f-assign" value={draft.assignment} onChange={(e) => set({ assignment: e.target.value as FilterState["assignment"] })}>
            <option value="anyone">Anyone</option>
            <option value="me">Me</option>
            <option value="unassigned">Unassigned</option>
            <option value="specific">Specific moderator…</option>
          </select>
        </span>
        {draft.assignment === "specific" ? (
          <span className="field" style={{ display: "inline-flex" }}>
            <label htmlFor="f-assignee">Moderator id</label>
            <input id="f-assignee" type="text" value={draft.assignedTo} onChange={(e) => set({ assignedTo: e.target.value })} />
          </span>
        ) : null}
      </fieldset>

      <fieldset>
        <legend>Date range</legend>
        <span className="field" style={{ display: "inline-flex", marginRight: 12 }}>
          <label htmlFor="f-openedafter">Opened after</label>
          <input id="f-openedafter" type="date" value={draft.openedAfter} onChange={(e) => set({ openedAfter: e.target.value })} />
        </span>
        <span className="field" style={{ display: "inline-flex", marginRight: 12 }}>
          <label htmlFor="f-openedbefore">Opened before</label>
          <input id="f-openedbefore" type="date" value={draft.openedBefore} onChange={(e) => set({ openedBefore: e.target.value })} />
        </span>
        <span className="field" style={{ display: "inline-flex", marginRight: 12 }}>
          <label htmlFor="f-lastafter">Last report after</label>
          <input id="f-lastafter" type="date" value={draft.lastReportAfter} onChange={(e) => set({ lastReportAfter: e.target.value })} />
        </span>
        <span className="field" style={{ display: "inline-flex" }}>
          <label htmlFor="f-lastbefore">Last report before</label>
          <input id="f-lastbefore" type="date" value={draft.lastReportBefore} onChange={(e) => set({ lastReportBefore: e.target.value })} />
        </span>
      </fieldset>

      <fieldset>
        <legend>Sort</legend>
        <span className="field" style={{ display: "inline-flex", marginRight: 12 }}>
          <label htmlFor="f-sortkey">Sort by</label>
          <select id="f-sortkey" value={draft.sortKey} onChange={(e) => set({ sortKey: e.target.value })}>
            {SORT_KEYS.map((k) => (
              <option key={k} value={k}>{k}</option>
            ))}
          </select>
        </span>
        <span className="field" style={{ display: "inline-flex" }}>
          <label htmlFor="f-sortdir">Direction</label>
          <select id="f-sortdir" value={draft.sortDir} onChange={(e) => set({ sortDir: e.target.value as "asc" | "desc" })}>
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </span>
      </fieldset>

      <div className="action-bar">
        <button type="submit">Apply filters</button>
        <button type="button" onClick={onReset}>Reset</button>
      </div>
    </form>
  );
}

// ── queue table ────────────────────────────────────────────────────────────────────────────────

function QueueTable({
  items,
  loading,
  now,
  selectedSubject,
  onOpen,
}: {
  items: CaseView[];
  loading: boolean;
  now: number;
  selectedSubject?: string;
  onOpen: (subjectId: string) => void;
}): ReactElement {
  if (loading) return <p role="status">Loading cases…</p>;
  if (items.length === 0) return <p>No cases match these filters.</p>;
  return (
    <table>
      <caption>Moderation queue</caption>
      <thead>
        <tr>
          <th scope="col">Subject</th>
          <th scope="col">Severity</th>
          <th scope="col">Categories</th>
          <th scope="col">Reports</th>
          <th scope="col">Distinct reporters</th>
          <th scope="col">Reporter trust</th>
          <th scope="col">Priority</th>
          <th scope="col">SLA</th>
          <th scope="col">Status</th>
          <th scope="col">Assignee</th>
          <th scope="col">Age</th>
        </tr>
      </thead>
      <tbody>
        {items.map((c) => {
          const sla = slaState(c.slaDueAt, now);
          return (
            <tr key={c.caseId} aria-current={selectedSubject === c.subjectId ? "true" : undefined}>
              <td>
                <button type="button" onClick={() => onOpen(c.subjectId)} title={c.subjectId}>
                  {shortId(c.subjectId)}
                </button>
                {c.hasCritical ? <span className="danger-high"> {c.escalated ? "⚑ escalated" : "critical"}</span> : null}
              </td>
              <td><SeverityBadge tier={c.severityTop} /></td>
              <td>{Object.keys(c.categories).join(", ") || "—"}</td>
              <td>{c.totalReports}</td>
              <td>{c.distinctReporters}</td>
              <td aria-label={`Trust-weighted reporter credibility: ${c.topReporterTrustBucket}`}>{c.topReporterTrustBucket}</td>
              <td>{Math.round(c.priority)}</td>
              <td className={sla.tone === "breached" ? "danger-high" : sla.tone === "due" ? "danger-medium" : undefined}>
                <span role={sla.tone === "breached" ? "status" : undefined}>{sla.text}</span>
              </td>
              <td>{c.status}</td>
              <td>{c.assignedTo ? shortId(c.assignedTo) : "unassigned"}</td>
              <td>{formatAge(c.openedAt, now)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── case panel ─────────────────────────────────────────────────────────────────────────────────

interface CasePerms {
  canAct: boolean;
  canAssign: boolean;
  canBanConfirm: boolean;
  canSubmitAppeal: boolean;
  canDecideAppeal: boolean;
  canViewEvidence: boolean;
  canLegalHold: boolean;
  canPreservationHold: boolean;
}

function CasePanel({
  data,
  currentActorId,
  perms,
  onClose,
  onNotice,
  onError,
  reloadCase,
  reloadQueue,
  call,
}: {
  data: CaseResponse;
  currentActorId?: string;
  perms: CasePerms;
  onClose: () => void;
  onNotice: (s: string | null) => void;
  onError: (s: string | null) => void;
  reloadCase: (subjectId: string) => Promise<CaseResponse | null>;
  reloadQueue: () => Promise<void>;
  call: FeatureScreenProps["call"];
}): ReactElement {
  const c = data.case;
  const now = Date.now();
  const sla = slaState(c.slaDueAt, now);
  // A support/welfare case (self-harm): the server returns self-harm resources only for such cases.
  const isSupportCase = data.supportResources.length > 0;
  // M2: evidence is suppressed ONLY for minorSafety-category cases, decided SERVER-SIDE and surfaced
  // as `case.evidenceSuppressed` (the evidence GET surface enforces the same rule, 409
  // EVIDENCE_SUPPRESSED). Escalated non-minorSafety cases (e.g. threats) stay viewable - evidence is
  // often essential there - so we must NOT key this off `escalated`.
  const evidenceSuppressed = c.evidenceSuppressed === true;

  const banProposer = pendingBanProposer(data.history);
  const originalActors = appealOriginalActors(data.history);

  async function runConflictAware(fn: () => Promise<unknown>): Promise<boolean> {
    onError(null);
    try {
      await fn();
      return true;
    } catch (e) {
      const status = (e as { status?: number }).status;
      const code = (e as { code?: string }).code;
      // Preservation-hold RULE violations (sticky / 180-day minimum / two-person) are 409s but NOT
      // version conflicts: the backend (or the portal) refused the change on its merits. Surface the
      // server's message verbatim (the same message whether the portal pre-check or Identity's 409
      // raised it) rather than the generic "reload and retry" conflict notice.
      if (code === "CHILD_SAFETY_STICKY" || code === "CHILD_SAFETY_MINIMUM" || code === "TWO_PERSON_REQUIRED") {
        onError(e instanceof Error ? e.message : "The action failed");
        return false;
      }
      if (status === 409 || code === "CASE_VERSION_CONFLICT" || code === "STATE_VERSION_CONFLICT" || code === "HOLD_VERSION_CONFLICT") {
        await reloadCase(c.subjectId);
        // LOW-2: a finalize that lost the race AFTER Identity already applied the account change is
        // NOT a failure - surface it as progress so the moderator does not re-apply the enforcement.
        if ((e as { applied?: boolean }).applied === true) {
          const applied = (e as { accountState?: { state?: string } }).accountState;
          const stateText = applied && typeof applied.state === "string" ? ` (${applied.state})` : "";
          onNotice(
            `The account change${stateText} was applied; the case is still updating. It has been reloaded with the latest version — no need to re-apply.`
          );
          return false;
        }
        onNotice("This case changed since you loaded it. It has been reloaded with the latest version — please review and try again.");
        return false;
      }
      onError(e instanceof Error ? e.message : "The action failed");
      return false;
    }
  }

  async function afterMutation(ok: boolean, message: string): Promise<void> {
    if (!ok) return;
    onNotice(message);
    await reloadCase(c.subjectId);
    await reloadQueue();
  }

  async function assign(): Promise<void> {
    const ok = await runConflictAware(() => call("assign", "POST", { subjectId: c.subjectId, caseVersion: c.version }));
    await afterMutation(ok, "Case assigned to you.");
  }
  async function unassign(): Promise<void> {
    const ok = await runConflictAware(() => call("unassign", "POST", { subjectId: c.subjectId, caseVersion: c.version }));
    await afterMutation(ok, "Case unassigned.");
  }
  async function submitAction(payload: Record<string, unknown>, message: string): Promise<void> {
    const ok = await runConflictAware(() => call("action", "POST", { subjectId: c.subjectId, caseVersion: c.version, ...payload }));
    await afterMutation(ok, message);
  }
  async function banConfirm(payload: Record<string, unknown>, message: string): Promise<void> {
    const ok = await runConflictAware(() => call("ban-confirm", "POST", { subjectId: c.subjectId, caseVersion: c.version, ...payload }));
    await afterMutation(ok, message);
  }
  async function appealSubmit(reason: string, reasonCode: string): Promise<void> {
    const ok = await runConflictAware(() => call("appeal-submit", "POST", { subjectId: c.subjectId, caseVersion: c.version, reason, reasonCode }));
    await afterMutation(ok, "Appeal recorded; the case is now awaiting re-review.");
  }
  async function appealDecide(decision: string, reason: string, reasonCode: string): Promise<void> {
    const ok = await runConflictAware(() => call("appeal-decide", "POST", { subjectId: c.subjectId, caseVersion: c.version, decision, reason, reasonCode }));
    await afterMutation(ok, `Appeal ${decision === "overturn" ? "overturned" : "upheld"}.`);
  }
  async function setLegalHold(evidenceId: string, hold: boolean, reason: string): Promise<void> {
    const ok = await runConflictAware(() =>
      call("evidence-legal-hold", "POST", { subjectId: c.subjectId, caseVersion: c.version, evidenceId, hold, reason })
    );
    await afterMutation(ok, hold ? "Legal hold placed on this evidence." : "Legal hold released on this evidence.");
  }
  async function setPreservationHold(reasonCode: string, reason: string, until?: number): Promise<void> {
    const body: Record<string, unknown> = { subjectId: c.subjectId, caseVersion: c.version, reasonCode, reason };
    if (until !== undefined) body.until = until;
    const ok = await runConflictAware(() => call("preservation-hold-set", "POST", body));
    await afterMutation(ok, "Preservation hold set.");
  }
  async function releasePreservationHold(reason: string): Promise<void> {
    const ok = await runConflictAware(() =>
      call("preservation-hold-release", "POST", { subjectId: c.subjectId, caseVersion: c.version, reason })
    );
    await afterMutation(ok, "Preservation hold released.");
  }

  return (
    <section aria-labelledby="case-title" className="page-help" style={{ borderLeftColor: c.hasCritical ? "#b3261e" : undefined }}>
      <div className="action-bar">
        <h2 id="case-title">Case {shortId(c.subjectId)}</h2>
        <button type="button" onClick={onClose}>Close case</button>
      </div>

      {c.hasCritical || c.escalated ? (
        <p role="alert" className="danger-high">
          <strong>{c.escalated ? "Escalated safety case" : "Critical case"}.</strong>{" "}
          {c.escalated
            ? "This case is treated as critical and routed to the safety lead. Handle with urgency; evidence controls are not available here."
            : "Critical severity — always top of queue and must reach a human."}
        </p>
      ) : null}

      {/* Subject summary + account state */}
      <h3>Subject</h3>
      <dl>
        <div className="detail-row"><dt>Subject id</dt><dd title={c.subjectId}>{shortId(c.subjectId)}</dd></div>
        <div className="detail-row"><dt>Account state</dt><dd>{c.accountState}{c.accountStateUntil ? ` (until ${fmtTime(c.accountStateUntil)})` : ""}</dd></div>
        <div className="detail-row"><dt>Preservation hold</dt><dd><PreservationHoldBadge hold={data.preservationHold ?? c.preservationHold} now={now} /></dd></div>
        <div className="detail-row"><dt>Status</dt><dd>{c.status} — {STATUS_HELP[c.status] ?? ""}</dd></div>
        <div className="detail-row"><dt>Severity</dt><dd><SeverityBadge tier={c.severityTop} /></dd></div>
        <div className="detail-row"><dt>Priority</dt><dd>{Math.round(c.priority)}</dd></div>
        <div className="detail-row"><dt>SLA</dt><dd className={sla.tone === "breached" ? "danger-high" : undefined}>{sla.text} · due {fmtTime(c.slaDueAt)}</dd></div>
        <div className="detail-row"><dt>Assignee</dt><dd>{c.assignedTo ? shortId(c.assignedTo) : "unassigned"}</dd></div>
        <div className="detail-row"><dt>Reports / distinct reporters / blockers</dt><dd>{c.totalReports} / {c.distinctReporters} / {c.distinctBlockers}</dd></div>
        <div className="detail-row"><dt>Reporter trust (top)</dt><dd>{c.topReporterTrustBucket}</dd></div>
        <div className="detail-row"><dt>Categories</dt><dd>{Object.entries(c.categories).map(([k, v]) => `${k} (${v})`).join(", ") || "—"}</dd></div>
        <div className="detail-row"><dt>Reciprocal</dt><dd>{c.hasReciprocal ? "yes — both parties reported each other (neither auto-penalised)" : "no"}</dd></div>
      </dl>

      <SignalsSection signals={data.signals} />

      {isSupportCase ? (
        <section aria-label="Support resources">
          <h3>Support resources</h3>
          <p className="field-hint">This is a welfare case. Punitive actions are not available; offer support and resolve.</p>
          <ul>
            {data.supportResources.map((r, i) => (
              <li key={i}>{r.name}: {r.contact}{r.region ? ` (${r.region})` : ""}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <ReportsSection reports={data.reports} />

      <section aria-label="History">
        <h3>History</h3>
        {data.history.length === 0 ? (
          <p>No history yet.</p>
        ) : (
          <ol>
            {[...data.history].sort((a, b) => b.at - a.at).map((h, i) => (
              <li key={i}>
                <time dateTime={new Date(h.at).toISOString()}>{fmtTime(h.at)}</time> — {shortId(h.actorId)}: <strong>{h.kind}</strong>
                {h.action ? ` (${h.action})` : ""}
                {h.reason ? ` — ${h.reason}` : ""}
              </li>
            ))}
          </ol>
        )}
      </section>

      {/* Evidence viewer (1c-ii): list uploaded decrypted-chat bundles, open one to re-check integrity
          server-side, read the you/them transcript, and (safety lead only) place a legal hold.
          Hidden (server-authoritative, M2) only for minorSafety cases via case.evidenceSuppressed,
          and without the view-evidence permission. Escalated non-minorSafety cases stay viewable. */}
      {perms.canViewEvidence && !evidenceSuppressed ? (
        <EvidenceSection
          evidence={c.evidence ?? []}
          canLegalHold={perms.canLegalHold}
          subjectId={c.subjectId}
          call={call}
          onSetLegalHold={setLegalHold}
        />
      ) : null}

      <PreservationHoldSection
        hold={data.preservationHold ?? c.preservationHold}
        canManage={perms.canPreservationHold}
        currentActorId={currentActorId}
        now={now}
        onSet={setPreservationHold}
        onRelease={releasePreservationHold}
      />

      <AppealSection
        appealStatus={c.appealStatus}
        canSubmit={perms.canSubmitAppeal}
        canDecide={perms.canDecideAppeal}
        decideBlocked={!!currentActorId && originalActors.has(currentActorId)}
        onSubmit={appealSubmit}
        onDecide={appealDecide}
      />

      {c.banProposalPending ? (
        <BanProposalSection
          proposer={banProposer}
          canConfirm={perms.canBanConfirm}
          isProposer={!!currentActorId && !!banProposer && currentActorId === banProposer}
          onConfirm={(reason, reasonCode) => void banConfirm({ decision: "confirm", reason, reasonCode }, "Ban confirmed.")}
          onReject={(reason, reasonCode) => void banConfirm({ decision: "reject", reason, reasonCode }, "Ban proposal rejected.")}
        />
      ) : null}

      <ActionPanel
        perms={perms}
        isSupportCase={isSupportCase}
        now={now}
        onAssign={assign}
        onUnassign={unassign}
        assignedToMe={!!currentActorId && c.assignedTo === currentActorId}
        onAction={submitAction}
      />
    </section>
  );
}

function SignalsSection({ signals }: { signals?: unknown }): ReactElement {
  const entries =
    signals && typeof signals === "object" && !Array.isArray(signals) ? Object.entries(signals as Record<string, unknown>) : [];
  return (
    <section aria-label="Signals">
      <h3>Signals</h3>
      {entries.length === 0 ? (
        <p>No additional signals available.</p>
      ) : (
        <ul>
          {entries.map(([k, v]) => (
            <li key={k}>{k}: {typeof v === "object" ? JSON.stringify(v) : String(v)}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ReportsSection({ reports }: { reports: ReportViewItem[] }): ReactElement {
  return (
    <section aria-label="Reports">
      <h3>Reports ({reports.length})</h3>
      {reports.length === 0 ? (
        <p>No reports on this case.</p>
      ) : (
        <ul className="action-list">
          {reports.map((r) => (
            <li key={r.reportId}>
              <details>
                <summary>
                  <SeverityBadge tier={r.severity} /> {r.category} · trust {r.reporterTrustBucket}
                  {r.evidenceOffered ? " · 📎 evidence offered" : ""}
                  {r.outcome ? ` · ${r.outcome}` : ""}
                </summary>
                <dl>
                  <div className="detail-row"><dt>Report id</dt><dd>{r.reportId}</dd></div>
                  <div className="detail-row"><dt>Reported at</dt><dd>{fmtTime(r.reportedAt)}</dd></div>
                  <div className="detail-row"><dt>Source</dt><dd>{r.source ?? "—"}</dd></div>
                  <div className="detail-row"><dt>Conversation</dt><dd>{r.conversationId ?? "—"}</dd></div>
                  <div className="detail-row"><dt>Also blocked</dt><dd>{r.alsoBlocked ? "yes" : "no"}</dd></div>
                  {r.detail !== undefined ? (
                    <div className="detail-row"><dt>Detail</dt><dd><pre>{JSON.stringify(r.detail, null, 2)}</pre></dd></div>
                  ) : null}
                </dl>
              </details>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── reason (code + note), shared by every action form ────────────────────────────────────────────

interface Reason {
  reasonCode: string;
  setReasonCode: (v: string) => void;
  note: string;
  setNote: (v: string) => void;
  valid: boolean;
  reset: () => void;
}

function useReason(): Reason {
  const [reasonCode, setReasonCode] = useState("");
  const [note, setNote] = useState("");
  return {
    reasonCode,
    setReasonCode,
    note,
    setNote,
    valid: note.trim().length >= 3,
    reset: () => {
      setReasonCode("");
      setNote("");
    },
  };
}

function ReasonFields({ idPrefix, codes, reason }: { idPrefix: string; codes: string[]; reason: Reason }): ReactElement {
  return (
    <>
      <span className="field">
        <label htmlFor={`${idPrefix}-code`}>Reason code</label>
        <select id={`${idPrefix}-code`} value={reason.reasonCode || codes[0]} onChange={(e) => reason.setReasonCode(e.target.value)}>
          {codes.map((code) => (
            <option key={code} value={code}>{code}</option>
          ))}
        </select>
      </span>
      <span className="field">
        <label htmlFor={`${idPrefix}-note`}>Reason note (required)</label>
        <textarea
          id={`${idPrefix}-note`}
          value={reason.note}
          aria-describedby={`${idPrefix}-note-hint`}
          onChange={(e) => reason.setNote(e.target.value)}
        />
        <span id={`${idPrefix}-note-hint`} className="field-hint">At least 3 characters. Recorded in the case history; never sent to the member.</span>
      </span>
    </>
  );
}

// ── action panel ─────────────────────────────────────────────────────────────────────────────────

function ActionPanel({
  perms,
  isSupportCase,
  now,
  onAssign,
  onUnassign,
  assignedToMe,
  onAction,
}: {
  perms: CasePerms;
  isSupportCase: boolean;
  now: number;
  onAssign: () => void;
  onUnassign: () => void;
  assignedToMe: boolean;
  onAction: (payload: Record<string, unknown>, message: string) => Promise<void>;
}): ReactElement {
  const [selectedAction, setSelectedAction] = useState<string>("dismiss");
  const reason = useReason();
  const [durationDays, setDurationDays] = useState("");
  const [until, setUntil] = useState("");
  const [confirming, setConfirming] = useState(false);

  const def = ACTION_DEFS.find((a) => a.id === selectedAction) ?? ACTION_DEFS[0];
  // Only show resolve-supported on support cases; hide all other support-only actions elsewhere.
  const available = ACTION_DEFS.filter((a) => (a.supportOnly ? isSupportCase : true));
  const punitiveBlocked = isSupportCase && def.punitive;
  const codes = REASON_CODES[def.id] ?? ["MOD_OTHER"];

  function durationFromUntil(): number | undefined {
    const ms = Date.parse(`${until}T00:00:00Z`);
    if (!Number.isFinite(ms)) return undefined;
    const days = Math.ceil((ms - now) / DAY_MS);
    return days > 0 ? days : undefined;
  }

  function validForSubmit(): boolean {
    if (!reason.valid || punitiveBlocked) return false;
    if (def.needsDuration) return (numOrUndef(durationDays) ?? 0) > 0;
    if (def.needsUntil) return (durationFromUntil() ?? 0) > 0;
    return true;
  }

  async function fire(): Promise<void> {
    const payload: Record<string, unknown> = { action: def.id, reason: reason.note, reasonCode: reason.reasonCode || codes[0] };
    if (def.needsDuration) payload.durationDays = numOrUndef(durationDays);
    if (def.needsUntil) payload.durationDays = durationFromUntil();
    await onAction(payload, `Action "${def.label}" applied.`);
    reason.reset();
    setDurationDays("");
    setUntil("");
    setConfirming(false);
  }

  function requestSubmit(): void {
    if (!validForSubmit()) return;
    if (def.confirm) setConfirming(true);
    else void fire();
  }

  if (!perms.canAct && !perms.canAssign) {
    return (
      <section aria-label="Actions">
        <h3>Actions</h3>
        <p className="field-hint">You have read-only access to this case.</p>
      </section>
    );
  }

  return (
    <section aria-label="Actions">
      <h3>Actions</h3>

      {perms.canAssign ? (
        <div className="action-bar">
          <button type="button" onClick={onAssign} disabled={assignedToMe}>Assign to me</button>
          <button type="button" onClick={onUnassign}>Unassign</button>
          {assignedToMe ? <span className="field-hint">Already assigned to you.</span> : null}
        </div>
      ) : null}

      {perms.canAct ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            requestSubmit();
          }}
        >
          <span className="field">
            <label htmlFor="action-select">Action</label>
            <select id="action-select" value={selectedAction} onChange={(e) => setSelectedAction(e.target.value)} aria-describedby="action-help">
              {available.map((a) => (
                <option key={a.id} value={a.id}>{a.label}</option>
              ))}
            </select>
            <span id="action-help" className="field-hint">{def.help}</span>
          </span>

          {punitiveBlocked ? (
            <p role="note" className="danger-medium" id="support-guard">
              Punitive actions are disabled on a support/welfare case. Use dismiss, escalate, request-info or resolve (support provided).
            </p>
          ) : null}

          {def.needsDuration ? (
            <span className="field">
              <label htmlFor="action-duration">Restrict for (days)</label>
              <input id="action-duration" type="number" min={1} value={durationDays} onChange={(e) => setDurationDays(e.target.value)} />
            </span>
          ) : null}

          {def.needsUntil ? (
            <span className="field">
              <label htmlFor="action-until">Suspended until</label>
              <input id="action-until" type="date" value={until} onChange={(e) => setUntil(e.target.value)} aria-describedby="action-until-hint" />
              <span id="action-until-hint" className="field-hint">Sign-in is blocked until this date.</span>
            </span>
          ) : null}

          <ReasonFields idPrefix="action" codes={codes} reason={reason} />

          <button type="submit" disabled={!validForSubmit()} aria-describedby={punitiveBlocked ? "support-guard" : undefined}>
            {def.confirm ? "Review…" : "Apply action"}
          </button>
        </form>
      ) : null}

      {confirming ? (
        <ConfirmDialog
          title={`Confirm: ${def.label}`}
          body={
            def.id === "propose-ban"
              ? "This proposes a permanent ban. A second moderator must confirm it before it takes effect."
              : "This blocks the member from signing in until the date you chose. Continue?"
          }
          confirmLabel="Confirm"
          onConfirm={() => void fire()}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </section>
  );
}

// ── ban proposal (two-person rule) ────────────────────────────────────────────────────────────────

function BanProposalSection({
  proposer,
  canConfirm,
  isProposer,
  onConfirm,
  onReject,
}: {
  proposer?: string;
  canConfirm: boolean;
  isProposer: boolean;
  onConfirm: (reason: string, reasonCode: string) => void;
  onReject: (reason: string, reasonCode: string) => void;
}): ReactElement {
  const reason = useReason();
  const [confirming, setConfirming] = useState(false);
  return (
    <section aria-label="Pending ban proposal" className="danger-high">
      <h3>Pending ban proposal</h3>
      <p>
        Proposed by <strong>{proposer ? shortId(proposer) : "another moderator"}</strong>. A permanent ban needs a second
        moderator (two-person rule).
      </p>
      {!canConfirm ? (
        <p className="field-hint">You do not hold the ban-confirm permission.</p>
      ) : isProposer ? (
        <p role="note" className="field-hint" id="proposer-block">
          You proposed this ban, so you cannot confirm it. A different moderator with the ban-confirm permission must confirm it.
        </p>
      ) : null}
      {canConfirm ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.valid && !isProposer) setConfirming(true);
          }}
        >
          <ReasonFields idPrefix="ban" codes={REASON_CODES["ban-confirm"]} reason={reason} />
          <div className="action-bar">
            <button
              type="submit"
              className="danger-high"
              disabled={isProposer || !reason.valid}
              aria-describedby={isProposer ? "proposer-block" : undefined}
            >
              Confirm ban
            </button>
            <button type="button" disabled={!reason.valid} onClick={() => onReject(reason.note, reason.reasonCode || REASON_CODES["ban-reject"][0])}>
              Reject proposal
            </button>
          </div>
          {confirming ? (
            <ConfirmDialog
              title="Confirm permanent ban"
              body="This permanently bans the member. It takes effect immediately and cannot be undone here."
              confirmLabel="Confirm ban"
              onConfirm={() => {
                onConfirm(reason.note, reason.reasonCode || REASON_CODES["ban-confirm"][0]);
                setConfirming(false);
              }}
              onCancel={() => setConfirming(false)}
            />
          ) : null}
        </form>
      ) : null}
    </section>
  );
}

// ── appeal ────────────────────────────────────────────────────────────────────────────────────────

function AppealSection({
  appealStatus,
  canSubmit,
  canDecide,
  decideBlocked,
  onSubmit,
  onDecide,
}: {
  appealStatus?: string;
  canSubmit: boolean;
  canDecide: boolean;
  decideBlocked: boolean;
  onSubmit: (reason: string, reasonCode: string) => void;
  onDecide: (decision: string, reason: string, reasonCode: string) => void;
}): ReactElement {
  const submitReason = useReason();
  const decideReason = useReason();
  const appealOpen = appealStatus === "open";
  return (
    <section aria-label="Appeal">
      <h3>Appeal</h3>
      <p>Status: <strong>{appealStatus ?? "none"}</strong></p>

      {canSubmit && !appealOpen ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (submitReason.valid) onSubmit(submitReason.note, submitReason.reasonCode || "MOD_APPEAL");
          }}
        >
          <ReasonFields idPrefix="appeal-submit" codes={["MOD_APPEAL", "MOD_OTHER"]} reason={submitReason} />
          <button type="submit" disabled={!submitReason.valid}>Record appeal</button>
        </form>
      ) : null}

      {canDecide && appealOpen ? (
        <form aria-label="Decide appeal">
          <ReasonFields idPrefix="appeal-decide" codes={REASON_CODES["appeal-decide"]} reason={decideReason} />
          {decideBlocked ? (
            <p role="note" className="field-hint" id="decide-block">
              You took the original action on this case, so you cannot decide its appeal. It must go to a different safety lead.
            </p>
          ) : null}
          <div className="action-bar">
            <button
              type="button"
              disabled={decideBlocked || !decideReason.valid}
              aria-describedby={decideBlocked ? "decide-block" : undefined}
              onClick={() => onDecide("uphold", decideReason.note, decideReason.reasonCode || "MOD_APPEAL")}
            >
              Uphold action
            </button>
            <button
              type="button"
              disabled={decideBlocked || !decideReason.valid}
              aria-describedby={decideBlocked ? "decide-block" : undefined}
              onClick={() => onDecide("overturn", decideReason.note, decideReason.reasonCode || "MOD_APPEAL")}
            >
              Overturn action
            </button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

// ── evidence viewer (permission view-evidence) + legal hold (permission legal-hold) ───────────────

/** ISO-8601 (with ms, UTC) rendered as a compact, readable timestamp. */
function fmtIso(iso: string): string {
  if (typeof iso !== "string") return "—";
  return iso.replace("T", " ").replace(/\.\d+Z$/, "Z");
}

/** Integrity verdict as TEXT (+ a symbol), never colour alone (a11y). The server re-hashes the bytes. */
function IntegrityBadge({ integrity }: { integrity: "verified" | "mismatch" }): ReactElement {
  const verified = integrity === "verified";
  return (
    <span className={verified ? undefined : "danger-high"} aria-label={verified ? "Integrity: verified" : "Integrity: hash mismatch"}>
      <span aria-hidden="true">{verified ? "✓ " : "⚠ "}</span>
      {verified ? "Verified" : "Hash mismatch"}
    </span>
  );
}

/**
 * Evidence viewer: lists each uploaded decrypted-chat bundle for the case and lets a permitted admin
 * open one (GET `evidence`, which re-hashes the object server-side and returns the integrity verdict,
 * the canonical transcript and the metadata) and, with the legal-hold permission, place/release a
 * legal hold (reason required + confirm dialog). Rendered only with view-evidence and only when the
 * case is NOT escalated (MINOR_SAFETY / critical-safety suppress evidence - gated by the caller).
 */
function EvidenceSection({
  evidence,
  canLegalHold,
  subjectId,
  call,
  onSetLegalHold,
}: {
  evidence: EvidenceSummary[];
  canLegalHold: boolean;
  subjectId: string;
  call: FeatureScreenProps["call"];
  onSetLegalHold: (evidenceId: string, hold: boolean, reason: string) => Promise<void>;
}): ReactElement {
  return (
    <section aria-label="Evidence">
      <h3>Evidence</h3>
      <p className="field-hint">
        A decrypted copy of the reported chat, shared from the reporter&apos;s device. Opening it re-checks the file against
        its recorded hash and is audited (who viewed which evidence).
      </p>
      {evidence.length === 0 ? (
        <p>No decrypted-chat evidence has been uploaded for this case.</p>
      ) : (
        <ul className="action-list">
          {evidence.map((e) => (
            <li key={e.evidenceId}>
              <EvidenceItem summary={e} canLegalHold={canLegalHold} subjectId={subjectId} call={call} onSetLegalHold={onSetLegalHold} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function EvidenceItem({
  summary,
  canLegalHold,
  subjectId,
  call,
  onSetLegalHold,
}: {
  summary: EvidenceSummary;
  canLegalHold: boolean;
  subjectId: string;
  call: FeatureScreenProps["call"];
  onSetLegalHold: (evidenceId: string, hold: boolean, reason: string) => Promise<void>;
}): ReactElement {
  const [detail, setDetail] = useState<EvidenceDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bodyId = `evidence-body-${summary.evidenceId}`;

  const open = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = (await call("evidence", "GET", { subjectId, evidenceId: summary.evidenceId })) as EvidenceDetail;
      setDetail(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not open this evidence.");
    } finally {
      setLoading(false);
    }
  }, [call, subjectId, summary.evidenceId]);

  const expanded = detail !== null;

  return (
    <article aria-label={`Evidence ${summary.evidenceId}`}>
      <div className="action-bar">
        <strong>{summary.evidenceId}</strong>
        <span className="field-hint">
          {summary.messageCount} message{summary.messageCount === 1 ? "" : "s"}
          {summary.includesMedia ? " · includes media" : ""}
          {summary.legalHold ? " · ⚑ legal hold" : ""}
        </span>
        <button type="button" onClick={() => (expanded ? setDetail(null) : void open())} aria-expanded={expanded} aria-controls={bodyId} disabled={loading}>
          {loading ? "Opening…" : expanded ? "Hide evidence" : "Open evidence"}
        </button>
      </div>

      {error ? <p role="alert">{error}</p> : null}

      {expanded && detail ? (
        <div id={bodyId}>
          <p>
            Integrity: <IntegrityBadge integrity={detail.integrity} />
            {detail.integrity === "mismatch" ? (
              <span role="status"> — the stored file does not match its recorded hash. Treat its contents with caution.</span>
            ) : null}
          </p>

          <h4>Details</h4>
          <dl>
            <div className="detail-row"><dt>Messages</dt><dd>{detail.bundle.messages.length}</dd></div>
            <div className="detail-row">
              <dt>Window</dt>
              <dd>
                {fmtTime(summary.firstSentAt)} → {fmtTime(summary.lastSentAt)} (cap {detail.bundle.window.maxMessages} messages / {detail.bundle.window.maxDays} days)
              </dd>
            </div>
            <div className="detail-row"><dt>Uploaded at</dt><dd>{fmtTime(summary.uploadedAt)}</dd></div>
            <div className="detail-row"><dt>Includes media</dt><dd>{summary.includesMedia ? "yes" : "no"}</dd></div>
          </dl>

          <h4>Transcript</h4>
          {detail.bundle.messages.length === 0 ? (
            <p>This bundle has no messages.</p>
          ) : (
            <ol className="evidence-transcript">
              {detail.bundle.messages.map((m) => {
                const mine = m.senderSide === "reporter";
                return (
                  <li key={m.messageId} className={mine ? "msg-you" : "msg-them"}>
                    <span className="msg-side">{mine ? "You (reporter)" : "Them (reported member)"}</span>{" "}
                    <time dateTime={m.sentAt}>{fmtIso(m.sentAt)}</time>
                    <div className="msg-text">{m.text}</div>
                  </li>
                );
              })}
            </ol>
          )}

          <h4>Legal hold</h4>
          <p>
            Status: <strong>{summary.legalHold ? "on" : "off"}</strong>
            {summary.retentionState ? ` · retention: ${summary.retentionState}` : ""}
          </p>
          {canLegalHold ? (
            <LegalHoldForm
              evidenceId={summary.evidenceId}
              legalHold={summary.legalHold}
              onSubmit={(reason) => onSetLegalHold(summary.evidenceId, !summary.legalHold, reason)}
            />
          ) : (
            <p className="field-hint">You do not hold the legal-hold permission.</p>
          )}
        </div>
      ) : null}
    </article>
  );
}

function LegalHoldForm({
  evidenceId,
  legalHold,
  onSubmit,
}: {
  evidenceId: string;
  legalHold: boolean;
  onSubmit: (reason: string) => Promise<void> | void;
}): ReactElement {
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const valid = reason.trim().length >= 3;
  const id = `lh-${evidenceId}`;
  const label = legalHold ? "Release legal hold" : "Place legal hold";
  return (
    <form
      aria-label={`Legal hold for ${evidenceId}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) setConfirming(true);
      }}
    >
      <span className="field">
        <label htmlFor={`${id}-reason`}>Legal-hold reason (required)</label>
        <textarea id={`${id}-reason`} value={reason} aria-describedby={`${id}-hint`} onChange={(e) => setReason(e.target.value)} />
        <span id={`${id}-hint`} className="field-hint">At least 3 characters. Recorded in the case history (audited).</span>
      </span>
      <button type="submit" disabled={!valid}>{label}</button>
      {confirming ? (
        <ConfirmDialog
          title={legalHold ? "Release legal hold" : "Place legal hold"}
          body={
            legalHold
              ? "This releases the legal hold on this evidence. It returns to the normal retention schedule (deleted after the case-close retention window)."
              : "This places a legal hold on this evidence. It will be retained regardless of the normal retention clock until the hold is released."
          }
          confirmLabel={label}
          onConfirm={() => {
            void onSubmit(reason);
            setConfirming(false);
            setReason("");
          }}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </form>
  );
}

// ── preservation hold (legal retention) badge + set/extend/release controls ───────────────────────

const PRESERVATION_REASON_CODES = ["CHILD_SAFETY", "LEGAL_REQUEST", "OTHER_LEGAL"] as const;
const PRESERVATION_DEFAULT_DAYS = 180;

/** Whether a mirrored hold is active at `now` (read-side expiry mirrors Identity's GET). */
function holdActiveNow(hold: PreservationHoldView | undefined, now: number): boolean {
  if (!hold || !hold.active) return false;
  return hold.until === undefined || hold.until > now;
}

function PreservationHoldBadge({ hold, now }: { hold?: PreservationHoldView; now: number }): ReactElement {
  if (!holdActiveNow(hold, now)) return <span>none</span>;
  const until = hold!.until ? ` · until ${fmtTime(hold!.until)}` : "";
  return (
    <strong className="danger-high" aria-label="preservation hold active">
      HELD{hold!.reasonCode ? ` · ${hold!.reasonCode}` : ""}{until}
    </strong>
  );
}

function PreservationHoldSection({
  hold,
  canManage,
  currentActorId,
  now,
  onSet,
  onRelease,
}: {
  hold?: PreservationHoldView;
  canManage: boolean;
  currentActorId?: string;
  now: number;
  onSet: (reasonCode: string, reason: string, until?: number) => Promise<void> | void;
  onRelease: (reason: string) => Promise<void> | void;
}): ReactElement {
  const active = holdActiveNow(hold, now);
  // Two-person rule (HIGH-2): the setter can NEVER release - a DIFFERENT safety lead must. There is
  // no override.
  const sameSetter = active && !!currentActorId && hold?.setBy === currentActorId;
  // MEDIUM-1: a CHILD_SAFETY hold cannot be released until 180 days after it was first set. Prefer
  // Identity's authoritative childSafetySince when present (task item 3), else the mirrored setAt.
  const childSafety = active && hold?.reasonCode === "CHILD_SAFETY";
  const childFirstSet = hold?.childSafetySince ?? hold?.setAt;
  const earliestReleaseAt =
    childSafety && typeof childFirstSet === "number" ? childFirstSet + PRESERVATION_DEFAULT_DAYS * 86_400_000 : undefined;
  const beforeMinimum = earliestReleaseAt !== undefined && now < earliestReleaseAt;
  return (
    <section aria-labelledby="ph-title">
      <h3 id="ph-title">Preservation hold (legal retention)</h3>
      <p>
        A preservation hold keeps this member's account, profile and related data from being erased for
        a legal / child-safety reason (Privacy Policy §5). While it is active a self-deletion is deferred
        and the case + its evidence never expire.
      </p>
      <p>
        Status: <PreservationHoldBadge hold={hold} now={now} />
        {active && hold?.setBy ? ` · set by ${shortId(hold.setBy)}` : ""}
      </p>
      {childSafety ? (
        <p className="field-hint">
          CHILD_SAFETY holds run for a minimum of {PRESERVATION_DEFAULT_DAYS} days and cannot be released
          before then{earliestReleaseAt !== undefined ? ` (not before ${fmtTime(earliestReleaseAt)})` : ""}.
          They are always released by two different safety leads.
        </p>
      ) : null}
      {!canManage ? (
        <p className="field-hint">You do not hold the preservation-hold permission (safety lead).</p>
      ) : (
        <>
          <PreservationHoldSetForm active={active} childSafetyActive={childSafety} onSubmit={onSet} />
          {active ? (
            <PreservationHoldReleaseForm sameSetter={sameSetter} beforeMinimum={beforeMinimum} onSubmit={onRelease} />
          ) : null}
        </>
      )}
    </section>
  );
}

function PreservationHoldSetForm({
  active,
  childSafetyActive,
  onSubmit,
}: {
  active: boolean;
  childSafetyActive: boolean;
  onSubmit: (reasonCode: string, reason: string, until?: number) => Promise<void> | void;
}): ReactElement {
  const [reasonCode, setReasonCode] = useState<string>(PRESERVATION_REASON_CODES[0]);
  const [reason, setReason] = useState("");
  const [untilDate, setUntilDate] = useState("");
  const [confirming, setConfirming] = useState(false);
  const valid = reason.trim().length >= 3;
  const label = active ? "Extend hold" : "Place hold";
  // MEDIUM A-1: CHILD_SAFETY is STICKY. While a CHILD_SAFETY hold is active the reason code is locked
  // to CHILD_SAFETY (an extend can never reclassify/downgrade it; the API refuses with 409).
  const effectiveReasonCode = childSafetyActive ? "CHILD_SAFETY" : reasonCode;
  function untilMs(): number | undefined {
    if (!untilDate) return undefined;
    const t = Date.parse(untilDate);
    return Number.isFinite(t) ? t : undefined;
  }
  return (
    <form
      aria-label="Set or extend preservation hold"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) setConfirming(true);
      }}
    >
      <span className="field">
        <label htmlFor="ph-code">Reason code</label>
        <select id="ph-code" value={effectiveReasonCode} disabled={childSafetyActive} aria-describedby={childSafetyActive ? "ph-code-hint" : undefined} onChange={(e) => setReasonCode(e.target.value)}>
          {PRESERVATION_REASON_CODES.map((rc) => (
            <option key={rc} value={rc}>{rc}</option>
          ))}
        </select>
        {childSafetyActive ? (
          <span id="ph-code-hint" className="field-hint">A CHILD_SAFETY hold is active; the reason code is locked to CHILD_SAFETY until it is released.</span>
        ) : null}
      </span>
      <span className="field">
        <label htmlFor="ph-until">Until (optional)</label>
        <input id="ph-until" type="date" value={untilDate} aria-describedby="ph-until-hint" onChange={(e) => setUntilDate(e.target.value)} />
        <span id="ph-until-hint" className="field-hint">Leave empty to default to {PRESERVATION_DEFAULT_DAYS} days. A hold is only ever extended forward.</span>
      </span>
      <span className="field">
        <label htmlFor="ph-reason">Reason note (required)</label>
        <textarea id="ph-reason" value={reason} aria-describedby="ph-reason-hint" onChange={(e) => setReason(e.target.value)} />
        <span id="ph-reason-hint" className="field-hint">At least 3 characters. Recorded in the case history (audited).</span>
      </span>
      <button type="submit" disabled={!valid}>{label}</button>
      {confirming ? (
        <ConfirmDialog
          title={label}
          body={`This places / extends a LEGAL preservation hold (${effectiveReasonCode}) on this member. Their self-deletion is deferred and the case + evidence are retained until the hold is released.`}
          confirmLabel={label}
          onConfirm={() => {
            void onSubmit(effectiveReasonCode, reason, untilMs());
            setConfirming(false);
            setReason("");
          }}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </form>
  );
}

function PreservationHoldReleaseForm({
  sameSetter,
  beforeMinimum,
  onSubmit,
}: {
  sameSetter: boolean;
  beforeMinimum: boolean;
  onSubmit: (reason: string) => Promise<void> | void;
}): ReactElement {
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  // HIGH-2: the setter can never release (no override). MEDIUM-1: a CHILD_SAFETY hold cannot be
  // released before its 180-day minimum. Either blocks the release button entirely.
  const blocked = sameSetter || beforeMinimum;
  const valid = reason.trim().length >= 3 && !blocked;
  return (
    <form
      aria-label="Release preservation hold"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) setConfirming(true);
      }}
    >
      {sameSetter ? (
        <p role="alert" className="danger-high">
          You set this hold. The two-person rule requires a DIFFERENT safety lead to release it. There is
          no override.
        </p>
      ) : null}
      {beforeMinimum ? (
        <p role="alert" className="danger-high">
          This CHILD_SAFETY hold is within its 180-day minimum and cannot be released yet.
        </p>
      ) : null}
      <span className="field">
        <label htmlFor="ph-release-reason">Release reason (required)</label>
        <textarea id="ph-release-reason" value={reason} aria-describedby="ph-release-hint" onChange={(e) => setReason(e.target.value)} />
        <span id="ph-release-hint" className="field-hint">At least 3 characters. Recorded in the case history (audited).</span>
      </span>
      <button type="submit" disabled={!valid}>Release hold</button>
      {confirming ? (
        <ConfirmDialog
          title="Release preservation hold"
          body="This releases the legal preservation hold. A deferred self-deletion can then complete, and the case retention clock restarts from the later of case close and the hold end."
          confirmLabel="Release hold"
          onConfirm={() => {
            void onSubmit(reason);
            setConfirming(false);
            setReason("");
          }}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </form>
  );
}

// ── accessible confirmation dialog (focus-managed) ────────────────────────────────────────────────

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}): ReactElement {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const titleId = "confirm-dialog-title";
  useEffect(() => {
    confirmRef.current?.focus();
  }, []);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="dialog"
      onKeyDown={(e) => {
        if (e.key === "Escape") onCancel();
      }}
    >
      <h3 id={titleId}>{title}</h3>
      <p>{body}</p>
      <div className="dialog-actions">
        <button type="button" ref={confirmRef} className="danger-high" onClick={onConfirm}>{confirmLabel}</button>
        <button type="button" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default ModerationScreen;
